import { useEffect, useMemo, useRef, useState } from "react";
import {
  useQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/react-query";
import { DoctorsClient, DoctorDto } from "../web-api-client";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { normalizeVi } from "../utils/normalize";
import { wrapFetchWithSignal } from "../utils/abort";
import { appConfig } from "../appconfig";

async function fetchDoctorsApi({
  date, // dd/MM/yyyy | ""
  availableOnly,
  onlyConfigRoom,
  roomId,
  pageNumber,
  pageSize,
  searchTerm,
  signal,
}: {
  date: string;
  availableOnly: boolean;
  onlyConfigRoom?: boolean;
  roomId?: number;
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  signal?: AbortSignal;
}) {
  const client = new DoctorsClient(appConfig.apiBaseUrl, {
    fetch: wrapFetchWithSignal(fetchWithTimeout(), signal),
  });

  return client.getDoctors(
    date || null,
    searchTerm?.trim() ? searchTerm.trim() : null,
    availableOnly,
    onlyConfigRoom,
    roomId,
    pageNumber ?? null,
    pageSize ?? null,
  );
}

export type UseDoctorsParams = {
  /** dd/MM/yyyy; empty string = "all days" */
  date: string;
  specialty: string | null;
  availableOnly: boolean;
  searchTerm: string;
  onlyConfigRoom?: boolean;
  roomId?: number;
  pageNumber?: number;
  pageSize?: number;
};

export type UseDoctorsResult = {
  rawDoctors: DoctorDto[];
  doctors: DoctorDto[];
  batchId?: string;

  /** initial load (show skeleton grid) */
  isLoading: boolean;
  /** background refetch (show tiny spinner, not blocking UI) */
  isFetching: boolean;

  isError: boolean;
  refetch: () => void;

  avatars: Record<string, string | null>;
  avatarErrors: Record<string, string | undefined>;

  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasNextPage: boolean;
};

export function useDoctors({
  date,
  specialty,
  availableOnly,
  searchTerm,
  onlyConfigRoom,
  roomId,
  pageNumber = 1,
  pageSize = 20,
}: UseDoctorsParams): UseDoctorsResult {
  const qc = useQueryClient();  

  // ===== Avatars via SSE =====
  const [avatars, setAvatars] = useState<Record<string, string | null>>({});
  const [avatarErrors, setAvatarErrors] = useState<
    Record<string, string | undefined>
  >({});
  const sseRef = useRef<EventSource | null>(null);

  // ===== SINGLE QUERY (no double API calls) =====
  const isFiltered = Boolean(date) || availableOnly;

  // Nếu không filter, ép availableOnly=false để khỏi backend lọc nhầm
  const serverDate = isFiltered ? date : "";
  const serverAvailableOnly = isFiltered ? availableOnly : false;

  const serverSearch = searchTerm?.trim() ?? "";

  const q = useQuery({
    queryKey: [
      "doctors",
      {
        date: serverDate,
        availableOnly: serverAvailableOnly,
        searchTerm: serverSearch, 
        specialty: specialty ?? null,
        onlyConfigRoom: !!onlyConfigRoom,
        roomId: roomId ?? null,
        pageNumber,
        pageSize,
      },
    ],
    queryFn: ({ signal }) =>
      fetchDoctorsApi({
        date: serverDate,
        availableOnly: serverAvailableOnly,
        onlyConfigRoom,
        roomId,
        pageNumber,
        pageSize,
        searchTerm: serverSearch,
        signal,
      }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const payload: any = q.data;

  const rawDoctors = useMemo<DoctorDto[]>(
    () => (payload?.doctors?.length ? payload.doctors : []),
    [payload?.doctors],
  );

  const batchId = payload?.batchId as string | undefined;

  const totalCount = payload?.totalCount ?? rawDoctors.length;
  const currentPageNumber = payload?.pageNumber ?? pageNumber;
  const currentPageSize = payload?.pageSize ?? pageSize;
  const hasNextPage = payload?.hasNextPage ?? false;

  // ===== Patch cache when SSE updates =====
  function applyProcessedUrlToCache(
    doctorId: string,
    processedUrl: string | undefined,
  ) {
    qc.setQueriesData({ queryKey: ["doctors"] }, (old: any) => {
      if (!old?.doctors?.length) return old;

      let changed = false;
      const nextDoctors = old.doctors.map((d: DoctorDto) => {
        if (String(d.doctorId) === String(doctorId)) {
          if ((d.processedUrl ?? null) !== (processedUrl ?? null)) {
            changed = true;
            return { ...d, processedUrl };
          }
        }
        return d;
      });

      return changed ? { ...old, doctors: nextDoctors } : old;
    });
  }

  function applyOriginalUrlToCache(
    doctorId: string,
    originalUrl: string | undefined,
  ) {
    qc.setQueriesData({ queryKey: ["doctors"] }, (old: any) => {
      if (!old?.doctors?.length) return old;

      let changed = false;
      const nextDoctors = old.doctors.map((d: DoctorDto) => {
        if (String(d.doctorId) === String(doctorId)) {
          if ((d.originalUrl ?? null) !== (originalUrl ?? null)) {
            changed = true;
            return { ...d, originalUrl };
          }
        }
        return d;
      });

      return changed ? { ...old, doctors: nextDoctors } : old;
    });
  }

  // Seed avatars map from current list
  useEffect(() => {
    if (!rawDoctors.length) return;
    setAvatars((prev) => {
      const next = { ...prev };
      for (const d of rawDoctors) {
        const key = String(d.doctorId);
        if (!(key in next)) next[key] = d.processedUrl ?? null;
      }
      return next;
    });
  }, [rawDoctors]);

  // ===== SSE: reopen whenever batchId changes =====
  useEffect(() => {
    // close if no batch
    if (!batchId) {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      return;
    }

    // close previous
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    const es = new EventSource(
      `${appConfig.apiBaseUrl}/api/Doctors/avatars/stream?batchId=${encodeURIComponent(batchId)}`,
    );
    sseRef.current = es;

    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data) as {
          doctorId?: string | number;
          processedUrl?: string;
          originalUrl?: string;
          imageProcessing?: boolean;
          error?: string;
        };

        const doctorId = ev.doctorId?.toString().trim();
        if (!doctorId) return;

        if (ev.error && ev.error.trim()) {
          setAvatars((prev) => ({ ...prev, [doctorId]: null }));
          setAvatarErrors((prev) => ({ ...prev, [doctorId]: ev.error }));
          return;
        }

        const url = ev.processedUrl?.toString().trim();
        const originalUrl = ev.originalUrl?.toString().trim();
        const processing = ev.imageProcessing;

        applyOriginalUrlToCache(doctorId, originalUrl);

        if (!url && processing && originalUrl) {
          setAvatars((prev) => ({ ...prev, [doctorId]: originalUrl || null }));
        } else if (url) {
          setAvatars((prev) => ({ ...prev, [doctorId]: url || null }));
        }

        applyProcessedUrlToCache(doctorId, url);
      } catch {
        // ignore bad events
      }
    };

    es.onerror = (err) => {
      console.error("SSE error", err);
    };

    return () => {
      es.close();
      sseRef.current = null;
    };
  }, [batchId]);
  

  // ===== Client-side filter: specialty + name =====
  const doctors = useMemo(() => {
    let list = rawDoctors;

    if (specialty && specialty.trim()) {
      const sp = specialty.trim().toLowerCase();
      list = list.filter((d) => (d.specialty ?? "").toLowerCase() === sp);
    }

    return list;
  }, [rawDoctors, specialty]);

  return {
    rawDoctors,
    doctors,
    batchId,

    isLoading: q.isPending, // initial load
    isFetching: q.isFetching, // background refetch

    isError: !!q.isError,
    refetch: () => q.refetch(),

    avatars,
    avatarErrors,

    totalCount,
    pageNumber: currentPageNumber,
    pageSize: currentPageSize,
    hasNextPage,
  };
}
